import DecisionEditModal from '@/decision/modals/DecisionEditModal'
import { EditorHandle } from '@/editor'
import SimpleEditor from '@/editor/components/SimpleEditor'
import MeetingEditModal from '@/meeting/modals/MeetingEditModal'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import TaskModal from '@/task/modals/TaskModal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  ThreadFragment,
  Thread_Status_Enum,
  Thread_Activity_Insert_Input,
  Thread_Activity_Type_Enum,
  Thread_Type_Enum,
  useCreateThreadActivityMutation,
} from '@gql'
import { cmdOrCtrlKey } from '@utils/env'
import { UserLocalStorageKeys } from '@utils/localStorage'
import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { MeetingContext } from '@/meeting/contexts/MeetingContext'
import useThreadStatus from '../hooks/useThreadStatus'
import { NextStepFormsContext } from '@/nextStep/contexts/NextStepFormsContext'
import { TaskFormsContext } from '@/task/contexts/TaskFormsContext'
import useCirclePermissions from '@/circle/hooks/useCirclePermissions'
import ProposalModal from '@/proposal/modals/ProposalModal'
import useCanPropose from '@/proposal/hooks/useCanPropose'
import {
  CreateIcon,
  DecisionIcon,
  MeetingIcon,
  CheckIcon,
  MoreIcon,
  RejectIcon,
  RotateLeftIcon,
  NextStepItemIcon,
  OrgChartIcon,
  PollIcon,
  SendIcon,
  TaskIcon,
  ThreadIcon,
} from 'src/icons'
import ActivityPollModal from '../modals/ActivityPollModal'
import ThreadEditModal from '../modals/ThreadEditModal'
import {
  isResolutionOffered,
  oppositeThreadType,
  ThreadResolution,
} from './threadResolutions'

interface Props extends BoxProps {
  thread: ThreadFragment
  // Closes the window that holds this thread, when there is one (REU02-B).
  onResolved?(): void
}

export default function ThreadActivityCreate({
  thread,
  onResolved,
  ...boxProps
}: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const currentMember = useCurrentMember()
  const { org } = useOrgContext()
  // A policy obeys the right to edit the circle carrying it (GOUV-14)
  const canAddDecision = useCirclePermissions(thread.circleId).canEditRole
  // Proposing is offered where the member sits in the circle that will decide.
  // The refusal is said here rather than met later as an editor where nothing
  // reacts — a silent refusal reads as a breakage (Yocoop, 09/08/2026).
  const { canPropose, refusal, decidingCircleNames } = useCanPropose(
    thread.circleId,
    thread
  )

  // Resolutions offered by this thread, according to its type
  const offersResolution = (resolution: ThreadResolution) =>
    isResolutionOffered(resolution, thread.type)

  const [createThreadActivity] = useCreateThreadActivityMutation()
  const editorRef = useRef<EditorHandle>(null)

  // White editor, with action buttons slightly lighter than the footer
  const editorBg = useColorModeValue('white', 'gray.900')
  const buttonBg = useColorModeValue('whiteAlpha.600', 'whiteAlpha.100')
  const actionButtonProps = {
    size: 'sm',
    variant: 'outline',
    flexShrink: 0,
    bg: buttonBg,
  } as const

  // Save message draft
  const draftKey = UserLocalStorageKeys.ThreadDrafts.replace('{id}', thread.id)
  const handleSaveDraft = useCallback(
    (value: string) => {
      localStorage.setItem(draftKey, value)
    },
    [draftKey]
  )

  // Restore message draft
  useEffect(() => {
    const draft = localStorage.getItem(draftKey)
    if (!draft) return
    // Discard legacy Lexical JSON drafts (markdown only)
    if (draft[0] === '{') {
      localStorage.removeItem(draftKey)
      return
    }
    setTimeout(() => {
      editorRef.current?.setValue(draft)
      editorRef.current?.editor?.commands.focus()
    }, 100)
  }, [draftKey])

  // Create a new activity
  const handleCreateActivity = useCallback(
    async (activity: Thread_Activity_Insert_Input) => {
      if (!org || !currentMember) return

      // Create activity
      await createThreadActivity({
        variables: {
          values: {
            threadId: thread.id,
            memberId: currentMember.id,
            data: {},
            ...activity,
          },
        },
      })
    },
    [org?.id, thread, currentMember?.id]
  )

  // Send message
  const handleSubmit = useCallback(async () => {
    if (!editorRef.current || editorRef.current?.isEmpty()) {
      return
    }

    // Get value
    const value = editorRef.current.getValue()

    // Clear editor
    editorRef.current?.clear()
    localStorage.setItem(draftKey, '')

    // Create message activity
    try {
      await handleCreateActivity({
        type: Thread_Activity_Type_Enum.Message,
        data: {
          message: value,
        },
      })
    } catch (error) {
      console.error(error)
      editorRef.current?.setValue(value)
      localStorage.setItem(draftKey, value)
    }
  }, [handleCreateActivity])

  // Poll
  const pollModal = useDisclosure()

  // Next step (G2-D2, G2-D4)
  //
  // The meeting is read from context, not from a prop: MeetingContext defaults
  // to undefined, and ThreadItem — which mounts this thread's modal — is
  // rendered inside the provider when we are in a meeting. React context
  // crosses portals, so the modal sees it. Outside a meeting the context is
  // undefined, and that absence IS the signal: no button.
  //
  // A finished meeting is no longer "in a meeting" either, hence isEnded —
  // same line as the board, which stops offering actions once the meeting ends
  // (G2-D17).
  const meetingState = useContext(MeetingContext)
  const meetingCircle = meetingState?.isEnded ? undefined : meetingState?.circle
  const offersNextStep =
    offersResolution(Thread_Activity_Type_Enum.NextStep) && !!meetingCircle

  // Validating or rejecting records the ANSWER to the facilitator's question
  // — "is your tension lifted?" — not the fact that we went past it (REU02-B1).
  // Same path as the status menu that already exists, so the same rights: these
  // buttons create no permission, they make an existing gesture obvious.
  const { threadStatus, setStatus } = useThreadStatus(thread)

  // Outside a meeting the single button flips to "Reopen" (GOUV1718-D7).
  // Read from the hook rather than from `thread`, so it follows the optimistic
  // local state and the label changes under the finger.
  const isClosed = threadStatus === Thread_Status_Enum.Closed

  const handleResolve = useCallback(
    async (status: Thread_Status_Enum) => {
      try {
        await setStatus(status)

      // Both outcomes leave a trace (GOUV1718-D9).
      //
      // 🔄 **Revokes REU02-B6**, which had validating leave none: a green icon
      // already said a tension was closed, where nothing said it had been set
      // aside. Yohan asked for symmetry on 14/08/2026 — a fil must read on its
      // own, without interpreting the colour of an icon, and two rules
      // depending on which button was pressed cannot be explained.
      //
      // ⚠️ The trace is a plain message, so its author can edit or delete it —
      // the assumed price of not adding an activity type, which would have cost
      // a migration and a codegen run.
      //
      // Reopening leaves nothing: it is not a resolution.
        if (status !== Thread_Status_Enum.Active) {
          const blocked = status === Thread_Status_Enum.Blocked
          const meetingTitle = meetingState?.meeting?.title
          await handleCreateActivity({
            type: Thread_Activity_Type_Enum.Message,
            data: {
              message: meetingTitle
                ? t(
                    blocked
                      ? 'ThreadActivityCreate.rejectedInMeeting'
                      : 'ThreadActivityCreate.validatedInMeeting',
                    { meeting: meetingTitle }
                  )
                : t(
                    blocked
                      ? 'ThreadActivityCreate.rejected'
                      : 'ThreadActivityCreate.validated'
                  ),
            },
          })
        }
      } catch (error) {
        // The window stays OPEN on a refusal (11/08): closing it would let the
        // person believe the tension was validated. Before, setStatus did not
        // await its mutation, so we closed on a promise kept in advance and a
        // refusal went unseen.
        console.error(error)
        toast({ title: t('common.error'), status: 'error', duration: 3000 })
        return
      }

      // In a meeting one decides, then moves on to the next tension.
      //
      // ⚠️ Never on a reopening (found by the GOUV-17/18 review): `ThreadModal`
      // passes `onResolved={onClose}`, and that modal opens from the tension
      // list — outside any meeting. Reopening a tension to work on it again
      // would close the window under the finger. Reopening is not resolving.
      if (status !== Thread_Status_Enum.Active) {
        onResolved?.()
      }
    },
    [
      setStatus,
      handleCreateActivity,
      meetingState?.meeting?.title,
      t,
      toast,
      onResolved,
    ]
  )

  // The button only ADDS a form; the forms themselves are rendered in the
  // activity stream, where a next step belongs (see NextStepFormsContext).
  const nextStepForms = useContext(NextStepFormsContext)

  // Proposal
  const proposalModal = useDisclosure()

  // Thread
  const [entityType, setEntityType] = useState<
    | Thread_Activity_Type_Enum.Thread
    | Thread_Activity_Type_Enum.Meeting
    | Thread_Activity_Type_Enum.Task
    | Thread_Activity_Type_Enum.Decision
  >(Thread_Activity_Type_Enum.Thread)
  const entityModal = useDisclosure()

  const handleEntityOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement> | Thread_Activity_Type_Enum) => {
      const type =
        typeof event === 'string'
          ? event
          : event.currentTarget.getAttribute('data-type')
      if (!type) return
      setEntityType(type as typeof entityType)
      entityModal.onOpen()
    },
    []
  )

  // In a meeting, a project is filled in a card of the stream, like a next
  // step; elsewhere, in the window (#123).
  const taskForms = useContext(TaskFormsContext)
  const handleTaskOpen = useCallback(() => {
    if (meetingCircle && taskForms) taskForms.addForm()
    else handleEntityOpen(Thread_Activity_Type_Enum.Task)
  }, [meetingCircle, taskForms, handleEntityOpen])

  const handleEntityCreated = useCallback(
    (id: string) => {
      const refField = `ref${entityType}Id`
      handleCreateActivity({ type: entityType, [refField]: id })
    },
    [handleCreateActivity, entityType]
  )

  // Sub-thread of the opposite type: escape hatch towards the resolutions
  // this thread does not offer. Undefined when opened by the plain button,
  // which must keep presuming nothing about the type.
  const oppositeType = oppositeThreadType(thread.type)
  const [subThreadType, setSubThreadType] = useState<Thread_Type_Enum>()

  const handleThreadOpen = useCallback(() => {
    setSubThreadType(undefined)
    handleEntityOpen(Thread_Activity_Type_Enum.Thread)
  }, [handleEntityOpen])

  const handleOppositeThreadOpen = useCallback(() => {
    setSubThreadType(oppositeType)
    handleEntityOpen(Thread_Activity_Type_Enum.Thread)
  }, [handleEntityOpen, oppositeType])

  if (thread.archivedAt) {
    return (
      <Box {...boxProps}>
        <Alert status="info" maxW="500px" m="0 auto">
          <AlertIcon />
          <AlertDescription>
            {t('ThreadActivityCreate.archived')}
          </AlertDescription>
        </Alert>
      </Box>
    )
  }

  return (
    <Box {...boxProps}>

      {/* Send sits ON the message line (REU02-B7), which is what frees the
          row below for Reject and Validate. Centred rather than top-aligned
          (Yohan, 11/08, after seeing it): on a message of several lines the
          button follows the middle of the block, which reads as aligned —
          top-aligned, it looked stuck to a corner. */}
      <Flex gap={2} align="center">
        {/* Wrapped: SimpleEditor takes no layout props of its own. */}
        <Box flex="1" minW={0}>
          <SimpleEditor
            ref={editorRef}
            placeholder={t('ThreadActivityCreate.placeholder')}
            value=""
            autoFocus
            maxH="50vh"
            bg={editorBg}
            onChange={handleSaveDraft}
            onSubmit={handleSubmit}
          />
        </Box>

        <Button
          colorScheme="blue"
          size="sm"
          flexShrink={0}
          rightIcon={<SendIcon variant="Bold" />}
          onClick={() => handleSubmit()}
        >
          {t(`common.send`)}
        </Button>
      </Flex>

      <HStack spacing={2} mt={2} align="center">
        <Flex gap={2} overflowX="auto" flex="1" py={1}>
          {offersResolution(Thread_Activity_Type_Enum.Proposal) && (
            <Tooltip
              label={
                refusal
                  ? // The reason comes from the rule, never rederived here: the
                    // two refusals ask different things of the reader — open the
                    // tension, or turn to someone who sits in the circle.
                    t(
                      refusal === 'roleLevel'
                        ? 'ThreadActivityCreate.proposalRefusedRoleLevel'
                        : 'ThreadActivityCreate.proposalRefused',
                      {
                        circles: decidingCircleNames.join(' · '),
                        count: decidingCircleNames.length,
                      }
                    )
                  : undefined
              }
              hasArrow
              isDisabled={canPropose}
            >
              <Box>
                <Button
                  {...actionButtonProps}
                  leftIcon={<OrgChartIcon size={20} />}
                  onClick={proposalModal.onOpen}
                  isDisabled={!canPropose}
                >
                  {t(`ThreadActivityCreate.proposal`)}
                </Button>
              </Box>
            </Tooltip>
          )}
          {offersResolution(Thread_Activity_Type_Enum.Decision) &&
            canAddDecision && (
              <Button
                {...actionButtonProps}
                leftIcon={<DecisionIcon size={20} />}
                onClick={() =>
                  handleEntityOpen(Thread_Activity_Type_Enum.Decision)
                }
              >
                {t(`common.createDecision`)}
              </Button>
            )}
          {offersResolution(Thread_Activity_Type_Enum.Task) && (
            <Button
              {...actionButtonProps}
              leftIcon={<TaskIcon size={20} />}
              onClick={handleTaskOpen}
            >
              {t(`common.createTask`)}
            </Button>
          )}
          {offersNextStep && (
            <Button
              {...actionButtonProps}
              leftIcon={<NextStepItemIcon size={20} />}
              onClick={nextStepForms?.addForm}
            >
              {t(`ThreadActivityCreate.nextStep`)}
            </Button>
          )}
          {/* Always rendered now (REU02-B7): it used to appear only for a thread
              having an opposite type, to offer the sub-thread. It now holds the
              three resolutions BOTH types share — poll, meeting, thread — which
              took the room without earning it. Each keeps its own condition: a
              type that does not offer one must not see it in the menu either. */}
          <Menu>
            <MenuButton
              as={IconButton}
              size="sm"
              variant="outline"
              flexShrink={0}
              bg={buttonBg}
              aria-label={t('ThreadActivityCreate.otherResolutions')}
              icon={<MoreIcon size={20} />}
            />
            <MenuList>
              {offersResolution(Thread_Activity_Type_Enum.Poll) && (
                <MenuItem
                  icon={<PollIcon size={20} />}
                  onClick={pollModal.onOpen}
                >
                  {t(`ThreadActivityCreate.poll`)}
                </MenuItem>
              )}
              {offersResolution(Thread_Activity_Type_Enum.Meeting) && (
                <MenuItem
                  icon={<MeetingIcon size={20} />}
                  onClick={() =>
                    handleEntityOpen(Thread_Activity_Type_Enum.Meeting)
                  }
                >
                  {t(`common.createMeeting`)}
                </MenuItem>
              )}
              {offersResolution(Thread_Activity_Type_Enum.Thread) && (
                <MenuItem
                  icon={<ThreadIcon size={20} />}
                  onClick={handleThreadOpen}
                >
                  {t(`common.createThread`)}
                </MenuItem>
              )}
              {oppositeType && (
                <MenuItem
                  icon={<ThreadIcon size={20} />}
                  onClick={handleOppositeThreadOpen}
                >
                  {t(`ThreadActivityCreate.subThread.${oppositeType}`)}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </Flex>


        {/* IN A MEETING — the pair the whole footer was rearranged for. Always
            shown, whatever the current status (plan decision, 11/08): a closed
            tension can be reopened, and a pair of buttons coming and going
            would add a rule to understand in the middle of a meeting. */}
        {/* Bigger than the resolutions on purpose (Yohan, 11/08): they are the
            two the facilitator points at in a meeting, and at `sm` without an
            icon they read as smaller than the buttons they sit next to. */}
        {meetingCircle ? (
          <>
            <Button
              size="md"
              px={6}
              flexShrink={0}
              colorScheme="orange"
              variant="outline"
              leftIcon={<RejectIcon size={18} />}
              onClick={() => handleResolve(Thread_Status_Enum.Blocked)}
            >
              {t('ThreadActivityCreate.reject')}
            </Button>
            <Button
              size="md"
              px={6}
              flexShrink={0}
              colorScheme="green"
              leftIcon={<CheckIcon size={18} />}
              onClick={() => handleResolve(Thread_Status_Enum.Closed)}
            >
              {t('ThreadActivityCreate.validate')}
            </Button>
          </>
        ) : (
          /* OUTSIDE A MEETING — one button only (GOUV1718-D6). The pair answers
             the facilitator's question, and alone at their screen nobody asks
             it: "rejecting" a tension one created oneself means nothing. What
             is left is finishing with it — or reopening it (D7).

             The 11/08 reason for keeping both buttons at all times falls here:
             nobody points at a screen during a session that is not happening.
             A button doing nothing would just read as broken.

             On BOTH kinds of tension (D6): the condition is the meeting, never
             `thread.type`. Two interfaces for one screen would be worse. */
          <Button
            size="md"
            px={6}
            flexShrink={0}
            colorScheme={isClosed ? 'gray' : 'green'}
            variant={isClosed ? 'outline' : 'solid'}
            leftIcon={
              isClosed ? <RotateLeftIcon size={18} /> : <CheckIcon size={18} />
            }
            onClick={() =>
              handleResolve(
                isClosed ? Thread_Status_Enum.Active : Thread_Status_Enum.Closed
              )
            }
          >
            {t(
              isClosed
                ? 'ThreadActivityCreate.reopen'
                : 'ThreadActivityCreate.finish'
            )}
          </Button>
        )}
      </HStack>

      {pollModal.isOpen && (
        <ActivityPollModal
          threadId={thread.id}
          isOpen
          onClose={pollModal.onClose}
        />
      )}

      {proposalModal.isOpen && (
        <ProposalModal
          threadId={thread.id}
          isOpen
          onClose={proposalModal.onClose}
        />
      )}

      {entityModal.isOpen && (
        <>
          {entityType === Thread_Activity_Type_Enum.Thread && (
            <ThreadEditModal
              defaults={{
                circleId: thread.circleId,
                type: subThreadType,
              }}
              intro={
                subThreadType &&
                t(`ThreadActivityCreate.subThreadIntro.${subThreadType}`, {
                  title: thread.title,
                })
              }
              isOpen
              onCreate={handleEntityCreated}
              onClose={entityModal.onClose}
            />
          )}

          {entityType === Thread_Activity_Type_Enum.Meeting && (
            <MeetingEditModal
              defaultCircleId={thread.circleId}
              isOpen
              onCreate={handleEntityCreated}
              onClose={entityModal.onClose}
            />
          )}

          {entityType === Thread_Activity_Type_Enum.Task && (
            <TaskModal
              defaults={{
                circleId: thread.circleId,
                memberId: currentMember?.id,
                title: thread.title,
              }}
              isOpen
              onCreate={handleEntityCreated}
              onClose={entityModal.onClose}
            />
          )}

          {entityType === Thread_Activity_Type_Enum.Decision && (
            <DecisionEditModal
              defaults={{
                circleId: thread.circleId,
                title: thread.title,
              }}
              isOpen
              onCreate={handleEntityCreated}
              onClose={entityModal.onClose}
            />
          )}
        </>
      )}
    </Box>
  )
}
